import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { firstValueFrom } from 'rxjs';
import { PdfToiResponse } from './models/pdf-toi.model';
import { ToiPdfGerado } from './models/toi-pdf-upload.model';
import { ToiDetalhe, ToiMidiaItem } from './models/toi-detalhe.model';
import { getToiMidiaKey } from './utils/toi-midia.util';
import { ToiService } from './toi.service';

export type ToiExportPdfPrepararElemento = () => Promise<HTMLElement | null>;
export type ToiExportPdfFinalizar = () => void;

/** Escala html2canvas para páginas de texto (formulário / informativo). */
const PDF_TEXTO_PAGE_SCALE = 1.5;
/** Escala html2canvas para páginas de fotos (menor que 2 para controlar tamanho). */
const PDF_FOTO_PAGE_SCALE = 1.25;
/** Qualidade JPEG das páginas de texto rasterizadas. */
const PDF_TEXTO_PAGE_JPEG_QUALITY = 0.92;
/** Qualidade JPEG das páginas de fotos rasterizadas. */
const PDF_FOTO_PAGE_JPEG_QUALITY = 0.87;
/**
 * Largura máxima das fotos antes da captura.
 * ~2× a largura exibida no layout A4 (357px) × escala 1.25 — suficiente para nitidez sem inflar o PDF.
 */
const PDF_FOTO_MAX_WIDTH_PX = 1200;
/** Qualidade JPEG das fotos pré-processadas (antes do html2canvas). */
const PDF_FOTO_JPEG_QUALITY = 0.88;
/** Largura máxima da imagem informativa pré-processada. */
const PDF_INFORMATIVO_MAX_WIDTH_PX = 1200;
const PDF_INFORMATIVO_JPEG_QUALITY = 0.88;

@Injectable()
export class ToiExportPdfService {
  constructor(
    private readonly messageService: MessageService,
    private readonly toiService: ToiService
  ) {}

  exportarPdf(
    pdfToi: PdfToiResponse | null | undefined,
    midiasOcultas: Set<string>,
    detalhe: ToiDetalhe | null | undefined,
    prepararElemento: ToiExportPdfPrepararElemento,
    finalizarRender: ToiExportPdfFinalizar
  ): void {
    if (!pdfToi && !detalhe) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Não há dados do TOI para exportar.',
        life: 3000,
      });
      return;
    }

    void this.executarExportacaoHtml(pdfToi, detalhe, prepararElemento, finalizarRender);
  }

  filterMidiasVisiveis(detalhe: ToiDetalhe, midiasOcultas: Set<string>): ToiMidiaItem[] {
    return (detalhe.midias ?? []).filter(
      (m, index) => !midiasOcultas.has(getToiMidiaKey(m, index))
    );
  }

  async gerarPdfFromElemento(
    element: HTMLElement | null,
    pdfToi: PdfToiResponse | null | undefined,
    detalhe: ToiDetalhe | null | undefined
  ): Promise<void> {
    const { blob, filename } = await this.montarPdfBlob(element, pdfToi, detalhe);
    this.baixarBlob(blob, filename);
  }

  async montarPdfBlob(
    element: HTMLElement | null,
    pdfToi: PdfToiResponse | null | undefined,
    detalhe: ToiDetalhe | null | undefined
  ): Promise<ToiPdfGerado> {
    if (!element?.innerHTML?.trim()) {
      throw new Error('Elemento do PDF vazio ou não encontrado.');
    }

    const numeroOs = detalhe?.numeroOS ?? pdfToi?.dadosHst?.numeroBa ?? 'TOI';
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      throw new Error('Não foi possível abrir a janela de impressão. Verifique o bloqueio de pop-ups.');
    }

    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((style) => (style as HTMLElement).outerHTML)
      .join('\n');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>TOI-${numeroOs}</title>
        <meta charset="utf-8">
        ${styles}
        <style>
          body {
            margin: 0;
            padding: 0;
            background: white;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 6px;
          }

          #conteudoParaPdfToi,
          #conteudoParaPdfToi * {
            font-family: Arial, Helvetica, sans-serif !important;
          }

          #conteudoParaPdfToi .toi-pdf-secao {
            margin: 0 8px 2px 8px;
            box-sizing: border-box;
          }

          #conteudoParaPdfToi .toi-pdf-secao > table {
            width: 100%;
            box-sizing: border-box;
          }

          #conteudoParaPdfToi .toi-pdf-secao-cabecalho {
            margin: 0 8px 0 8px;
            box-sizing: border-box;
          }

          #conteudoParaPdfToi .toi-pdf-secao-fotos {
            margin: 10px 8px;
            box-sizing: border-box;
          }

          #conteudoParaPdfToi .toi-pdf-secao-fotos-conteudo {
            margin: 0 8px 10px 8px;
            box-sizing: border-box;
          }
        </style>
      </head>
      <body>${element.outerHTML}</body>
      </html>
    `);
    printWindow.document.close();

    this.normalizarUrlsImagens(printWindow.document);
    await this.aguardar(500);
    await this.aguardarImagens(printWindow.document);
    await this.comprimirImagensParaPdf(printWindow.document);
    await this.aguardar(300);

    const bodyElement = printWindow.document.body;
    const paginas = Array.from(bodyElement.querySelectorAll('.toi-pdf-pagina')) as HTMLElement[];

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
    pdf.setPage(1);

    const scaleFactor = 0.9;
    const imgWidth = 210 * scaleFactor;
    const pageHeight = 297;
    const pageWidth = 210;
    const marginX = (pageWidth - imgWidth) / 2;

    if (paginas.length > 0) {
      await this.gerarPdfPorPaginas(pdf, paginas, imgWidth, pageHeight, marginX);
    } else {
      const canvas = await html2canvas(bodyElement, {
        scale: PDF_TEXTO_PAGE_SCALE,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: false,
        windowWidth: bodyElement.scrollWidth,
        windowHeight: bodyElement.scrollHeight,
      });

      if (canvas.width === 0 || canvas.height === 0) {
        printWindow.close();
        throw new Error('Falha ao capturar o conteúdo do PDF.');
      }

      const pageBreakElements = this.detectarElementosQuebraPagina(bodyElement, printWindow);
      if (pageBreakElements.length > 0) {
        this.gerarPdfComQuebras(
          pdf,
          canvas,
          bodyElement,
          pageBreakElements,
          imgWidth,
          pageHeight,
          marginX,
          PDF_TEXTO_PAGE_JPEG_QUALITY
        );
      } else {
        this.gerarPdfSemQuebras(pdf, canvas, imgWidth, pageHeight, marginX, PDF_TEXTO_PAGE_JPEG_QUALITY);
      }
    }

    const filename = `${numeroOs}.pdf`;
    const blob = pdf.output('blob');
    printWindow.close();
    return { blob, filename };
  }

  async gerarEEnviarPdfOficial(params: {
    idAtividadeToi: number;
    pdfToi: PdfToiResponse;
    detalhe: ToiDetalhe;
    prepararElemento: ToiExportPdfPrepararElemento;
    finalizarRender: ToiExportPdfFinalizar;
    mensagemSucessoNegocio?: string;
  }): Promise<void> {
    const {
      idAtividadeToi,
      pdfToi,
      detalhe,
      prepararElemento,
      finalizarRender,
      mensagemSucessoNegocio,
    } = params;

    this.exibirMensagemAguarde(true, 'Gerando e enviando PDF do TOI, por favor, aguarde...');

    try {
      const element = await prepararElemento();
      const { blob, filename } = await this.montarPdfBlob(element, pdfToi, detalhe);
      await firstValueFrom(this.toiService.enviarPdfToi(idAtividadeToi, blob, filename));

      this.exibirMensagemAguarde(false);
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: mensagemSucessoNegocio
          ? `${mensagemSucessoNegocio} PDF enviado com sucesso.`
          : 'PDF do TOI enviado com sucesso.',
        life: 4000,
      });
    } catch (error) {
      this.exibirMensagemAguarde(false);
      const detalheErro =
        error instanceof Error ? error.message : 'Não foi possível gerar ou enviar o PDF do TOI.';
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: mensagemSucessoNegocio
          ? `${mensagemSucessoNegocio} Porém houve falha ao enviar o PDF: ${detalheErro}`
          : detalheErro,
        life: 6000,
      });
      console.error('[TOI PDF upload]', error);
    } finally {
      finalizarRender();
    }
  }

  private baixarBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  /** Captura cada `.toi-pdf-pagina` individualmente com escala/qualidade adequadas ao tipo de conteúdo. */
  private async gerarPdfPorPaginas(
    pdf: jsPDF,
    paginas: HTMLElement[],
    imgWidth: number,
    pageHeight: number,
    marginX: number
  ): Promise<void> {
    for (let i = 0; i < paginas.length; i++) {
      const pagina = paginas[i];
      const isFotoPage = pagina.classList.contains('toi-pdf-fotos-pagina');
      const scale = isFotoPage ? PDF_FOTO_PAGE_SCALE : PDF_TEXTO_PAGE_SCALE;
      const jpegQuality = isFotoPage ? PDF_FOTO_PAGE_JPEG_QUALITY : PDF_TEXTO_PAGE_JPEG_QUALITY;

      const canvas = await html2canvas(pagina, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: false,
        width: pagina.scrollWidth,
        height: pagina.scrollHeight,
        windowWidth: pagina.scrollWidth,
        windowHeight: pagina.scrollHeight,
      });

      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('Falha ao capturar página do PDF.');
      }

      this.adicionarPaginaUnicaAoPdf(
        pdf,
        canvas,
        imgWidth,
        pageHeight,
        marginX,
        i === 0,
        jpegQuality
      );
    }
  }

  private detectarElementosQuebraPagina(bodyElement: HTMLElement, printWindow: Window): HTMLElement[] {
    const marcados = bodyElement.querySelectorAll('.toi-pdf-pagina-break');
    if (marcados.length > 0) {
      return Array.from(marcados) as HTMLElement[];
    }

    const fotos = bodyElement.querySelectorAll('.toi-pdf-fotos-pagina');
    if (fotos.length > 0) {
      return Array.from(fotos) as HTMLElement[];
    }

    return Array.from(bodyElement.querySelectorAll('*')).filter((el: Element) => {
      const htmlEl = el as HTMLElement;
      const style = htmlEl.getAttribute('style') || '';
      return (
        style.includes('page-break-before') ||
        htmlEl.classList.contains('page-break') ||
        printWindow.getComputedStyle(htmlEl).pageBreakBefore === 'always'
      );
    }) as HTMLElement[];
  }

  private gerarPdfComQuebras(
    pdf: jsPDF,
    canvas: HTMLCanvasElement,
    bodyElement: HTMLElement,
    pageBreakElements: HTMLElement[],
    imgWidth: number,
    pageHeight: number,
    marginX: number,
    jpegQuality: number
  ): void {
    const bodyRect = bodyElement.getBoundingClientRect();
    const breaksWithPositions = pageBreakElements
      .map((htmlEl) => {
        const rect = htmlEl.getBoundingClientRect();
        const breakPositionPx = rect.top - bodyRect.top + bodyElement.scrollTop;
        const breakPositionInCanvas = Math.round(
          (breakPositionPx / bodyElement.scrollHeight) * canvas.height
        );
        return { positionInCanvas: breakPositionInCanvas };
      })
      .filter((item, index, arr) => index === 0 || item.positionInCanvas > arr[index - 1].positionInCanvas)
      .sort((a, b) => a.positionInCanvas - b.positionInCanvas);

    let lastBreakPosition = 0;
    let isFirstPdfPage = true;

    for (const currentBreak of breaksWithPositions) {
      const sectionStart = lastBreakPosition;
      const sectionEnd = currentBreak.positionInCanvas;
      const sectionHeight = sectionEnd - sectionStart;

      if (sectionHeight > 0) {
        const sectionCanvas = this.extrairSecaoCanvas(canvas, sectionStart, sectionHeight);
        if (sectionCanvas) {
          this.adicionarPaginaUnicaAoPdf(
            pdf,
            sectionCanvas,
            imgWidth,
            pageHeight,
            marginX,
            isFirstPdfPage,
            jpegQuality
          );
          isFirstPdfPage = false;
        }
      }

      lastBreakPosition = sectionEnd;
    }

    const lastSectionHeight = canvas.height - lastBreakPosition;
    if (lastSectionHeight > 0) {
      const lastSectionCanvas = this.extrairSecaoCanvas(canvas, lastBreakPosition, lastSectionHeight);
      if (lastSectionCanvas) {
        this.adicionarPaginaUnicaAoPdf(
          pdf,
          lastSectionCanvas,
          imgWidth,
          pageHeight,
          marginX,
          isFirstPdfPage,
          jpegQuality
        );
      }
    }
  }

  private gerarPdfSemQuebras(
    pdf: jsPDF,
    canvas: HTMLCanvasElement,
    imgWidth: number,
    pageHeight: number,
    marginX: number,
    jpegQuality: number
  ): void {
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    const dataUrl = canvas.toDataURL('image/jpeg', jpegQuality);
    pdf.addImage(dataUrl, 'JPEG', marginX, position, imgWidth, imgHeight, undefined, 'MEDIUM');
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(dataUrl, 'JPEG', marginX, position, imgWidth, imgHeight, undefined, 'MEDIUM');
      heightLeft -= pageHeight;
    }
  }

  private extrairSecaoCanvas(
    canvas: HTMLCanvasElement,
    sectionStart: number,
    sectionHeight: number
  ): HTMLCanvasElement | null {
    const start = Math.max(0, Math.round(sectionStart));
    const height = Math.min(Math.round(sectionHeight), canvas.height - start);
    if (height <= 0) {
      return null;
    }

    const sectionCanvas = document.createElement('canvas');
    sectionCanvas.width = canvas.width;
    sectionCanvas.height = height;
    const ctx = sectionCanvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    ctx.drawImage(canvas, 0, start, canvas.width, height, 0, 0, canvas.width, height);
    return sectionCanvas;
  }

  private adicionarPaginaUnicaAoPdf(
    pdf: jsPDF,
    sectionCanvas: HTMLCanvasElement,
    imgWidth: number,
    pageHeight: number,
    marginX: number,
    isFirstPdfPage: boolean,
    jpegQuality: number
  ): void {
    if (!isFirstPdfPage) {
      pdf.addPage();
    }

    const sectionAspect = sectionCanvas.width / sectionCanvas.height;
    const pageAspect = imgWidth / pageHeight;

    let renderWidth = imgWidth;
    let renderHeight = pageHeight;

    if (sectionAspect > pageAspect) {
      renderHeight = imgWidth / sectionAspect;
    } else {
      renderWidth = pageHeight * sectionAspect;
    }

    const offsetX = marginX + (imgWidth - renderWidth) / 2;
    const offsetY = Math.max(0, (pageHeight - renderHeight) / 2);
    const dataUrl = sectionCanvas.toDataURL('image/jpeg', jpegQuality);
    pdf.addImage(dataUrl, 'JPEG', offsetX, offsetY, renderWidth, renderHeight, undefined, 'MEDIUM');
  }

  private async executarExportacaoHtml(
    pdfToi: PdfToiResponse | null | undefined,
    detalhe: ToiDetalhe | null | undefined,
    prepararElemento: ToiExportPdfPrepararElemento,
    finalizarRender: ToiExportPdfFinalizar
  ): Promise<void> {
    this.exibirMensagemAguarde(true);

    try {
      const element = await prepararElemento();
      await this.gerarPdfFromElemento(element, pdfToi, detalhe);

      this.exibirMensagemAguarde(false);
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'PDF do TOI exportado com sucesso.',
        life: 3000,
      });
    } catch {
      this.exibirMensagemAguarde(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Não foi possível gerar o PDF do TOI.',
        life: 4000,
      });
    } finally {
      finalizarRender();
    }
  }

  private exibirMensagemAguarde(exibir: boolean, detail = 'Gerando PDF do TOI, por favor, aguarde...'): void {
    if (exibir) {
      this.messageService.add({
        severity: 'info',
        summary: 'Aguarde',
        detail,
        life: 6000,
      });
    } else {
      this.messageService.clear();
    }
  }

  private aguardar(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private normalizarUrlsImagens(documento: Document): void {
    const base = `${window.location.origin}/`;

    documento.querySelectorAll('img').forEach((img) => {
      const src = img.getAttribute('src');
      if (!src || src.startsWith('data:') || src.startsWith('http://') || src.startsWith('https://')) {
        return;
      }

      img.setAttribute('src', new URL(src.replace(/^\//, ''), base).href);
    });
  }

  /** Reduz resolução das fotos e do informativo antes do html2canvas capturar a página. */
  private async comprimirImagensParaPdf(documento: Document): Promise<void> {
    const tarefas: Promise<void>[] = [];

    documento.querySelectorAll('.toi-pdf-fotos-pagina img').forEach((img) => {
      tarefas.push(
        this.comprimirImagemElemento(
          img as HTMLImageElement,
          PDF_FOTO_MAX_WIDTH_PX,
          PDF_FOTO_JPEG_QUALITY
        )
      );
    });

    documento.querySelectorAll('.toi-pdf-informativo-pagina img').forEach((img) => {
      tarefas.push(
        this.comprimirImagemElemento(
          img as HTMLImageElement,
          PDF_INFORMATIVO_MAX_WIDTH_PX,
          PDF_INFORMATIVO_JPEG_QUALITY
        )
      );
    });

    await Promise.all(tarefas);
  }

  private async comprimirImagemElemento(
    img: HTMLImageElement,
    maxWidth: number,
    quality: number
  ): Promise<void> {
    if (img.naturalWidth === 0) {
      return;
    }

    try {
      const dataUrl = this.redimensionarElementoImagemParaJpeg(img, maxWidth, quality);
      img.src = dataUrl;
      img.removeAttribute('srcset');
    } catch {
      /* mantém imagem original se o canvas estiver tainted ou indisponível */
    }
  }

  private redimensionarElementoImagemParaJpeg(
    imagem: HTMLImageElement,
    maxWidth: number,
    quality: number
  ): string {
    const ratio = Math.min(1, maxWidth / imagem.naturalWidth);
    const largura = Math.max(1, Math.round(imagem.naturalWidth * ratio));
    const altura = Math.max(1, Math.round(imagem.naturalHeight * ratio));

    const canvas = document.createElement('canvas');
    canvas.width = largura;
    canvas.height = altura;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Canvas indisponível');
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, largura, altura);
    ctx.drawImage(imagem, 0, 0, largura, altura);

    return canvas.toDataURL('image/jpeg', quality);
  }

  private aguardarImagens(documento: Document): Promise<void> {
    const images = Array.from(documento.querySelectorAll('img'));

    return Promise.all(
      images.map(
        (img) =>
          new Promise<void>((resolve) => {
            const htmlImg = img as HTMLImageElement;

            const finalizar = () => {
              if (htmlImg.naturalWidth > 0) {
                resolve();
                return;
              }

              const src = htmlImg.getAttribute('src');
              if (src && !src.startsWith('data:') && !src.startsWith('http')) {
                const absoluto = new URL(src.replace(/^\//, ''), `${window.location.origin}/`).href;
                if (absoluto !== htmlImg.src) {
                  const timeout = setTimeout(() => resolve(), 8000);
                  htmlImg.onload = () => {
                    clearTimeout(timeout);
                    resolve();
                  };
                  htmlImg.onerror = () => {
                    clearTimeout(timeout);
                    resolve();
                  };
                  htmlImg.src = absoluto;
                  return;
                }
              }

              resolve();
            };

            if (htmlImg.complete) {
              finalizar();
              return;
            }

            const timeout = setTimeout(() => resolve(), 8000);
            htmlImg.onload = () => {
              clearTimeout(timeout);
              finalizar();
            };
            htmlImg.onerror = () => {
              clearTimeout(timeout);
              resolve();
            };
          })
      )
    ).then(() => undefined);
  }
}
